import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSnackbar } from 'notistack';
import CompanyInfoForm from '../../components/companyinfomng/CompanyInfoForm';
import * as companyInfoApi from '../../lib/api/companyInfo';

const CompanyInfoContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [logoBlobUrl, setLogoBlobUrl] = useState(null);
  const [sealBlobUrl, setSealBlobUrl] = useState(null);
  // 이전 Blob URL 정리용 ref
  const prevLogoBlobUrl = useRef(null);
  const prevSealBlobUrl = useRef(null);

  // Blob URL 업데이트 (이전 URL 메모리 해제)
  const updateLogoBlobUrl = useCallback((url) => {
    if (prevLogoBlobUrl.current) URL.revokeObjectURL(prevLogoBlobUrl.current);
    prevLogoBlobUrl.current = url;
    setLogoBlobUrl(url);
  }, []);

  const updateSealBlobUrl = useCallback((url) => {
    if (prevSealBlobUrl.current) URL.revokeObjectURL(prevSealBlobUrl.current);
    prevSealBlobUrl.current = url;
    setSealBlobUrl(url);
  }, []);

  // 회사 정보 조회
  const fetchCompanyInfo = useCallback(async () => {
    setLoading(true);
    try {
      const response = await companyInfoApi.getCompanyInfo();
      const info = response.data.info || {};
      setCompanyInfo(info);
      setHasChanges(false);
      // 로고/인감 있으면 Blob URL 조회
      if (info.logoFilePath) {
        companyInfoApi.fetchLogoImageBlobUrl().then(updateLogoBlobUrl);
      }
      if (info.sealFilePath) {
        companyInfoApi.fetchSealImageBlobUrl().then(updateSealBlobUrl);
      }
    } catch (error) {
      console.error('Failed to fetch company info:', error);
      setCompanyInfo({});
    } finally {
      setLoading(false);
    }
  }, [updateLogoBlobUrl, updateSealBlobUrl]);

  // 초기 로드
  useEffect(() => {
    fetchCompanyInfo();
  }, [fetchCompanyInfo]);

  // 필드 변경 핸들러
  const handleChange = (field, value) => {
    setCompanyInfo(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  // 저장 핸들러
  const handleSave = async () => {
    if (!companyInfo.companyNm) {
      enqueueSnackbar('회사명은 필수 입력 항목입니다.', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      await companyInfoApi.saveCompanyInfo(companyInfo);
      enqueueSnackbar('회사 정보가 저장되었습니다.', { variant: 'success' });
      setHasChanges(false);
      // 저장 후 다시 조회
      await fetchCompanyInfo();
    } catch (error) {
      console.error('Failed to save company info:', error);
      enqueueSnackbar('저장에 실패했습니다.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // 초기화 핸들러
  const handleReset = () => {
    if (window.confirm('변경사항을 취소하고 다시 불러오시겠습니까?')) {
      fetchCompanyInfo();
    }
  };

  // 로고 파일 업로드
  const handleLogoUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      await companyInfoApi.uploadLogo(formData);
      enqueueSnackbar('로고가 업로드되었습니다.', { variant: 'success' });
      // 업로드 후 서버에서 최신 정보 재조회
      await fetchCompanyInfo();
      // 헤더 로고 즉시 갱신: 새 Blob URL 생성 후 이벤트로 전달
      const newBlobUrl = await companyInfoApi.fetchLogoImageBlobUrl();
      window.dispatchEvent(new CustomEvent('company-logo-updated', { detail: { blobUrl: newBlobUrl } }));
    } catch (error) {
      console.error('Failed to upload logo:', error);
      enqueueSnackbar('로고 업로드에 실패했습니다.', { variant: 'error' });
    }
  };

  // 인감 파일 업로드
  const handleSealUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      await companyInfoApi.uploadSeal(formData);
      enqueueSnackbar('인감이 업로드되었습니다.', { variant: 'success' });
      // 업로드 후 서버에서 최신 정보 재조회
      await fetchCompanyInfo();
    } catch (error) {
      console.error('Failed to upload seal:', error);
      enqueueSnackbar('인감 업로드에 실패했습니다.', { variant: 'error' });
    }
  };

  return (
    <CompanyInfoForm
      companyInfo={companyInfo}
      loading={loading}
      saving={saving}
      hasChanges={hasChanges}
      onChange={handleChange}
      onSave={handleSave}
      onReset={handleReset}
      onLogoUpload={handleLogoUpload}
      onSealUpload={handleSealUpload}
      logoBlobUrl={logoBlobUrl}
      sealBlobUrl={sealBlobUrl}
    />
  );
};

export default CompanyInfoContainer;
